<template>
  <div id="activatedTable" class="data-card card">
    <div class="data-container">
      <div class="button-header">
        <Button class="icon icon-left outline-white light-text" :loading="csvIsLoading" :label="$t('app.campaign.results.headerButtons.exportCSV')" v-tooltip="$t('app.campaign.results.headerButtons.exportCSVTooltip')" @click="exportCSV">
          <template #prefix>
            <ExportCsvButtonIcon style="width: 16px"></ExportCsvButtonIcon>
          </template>
        </Button>
        <DropdownButton :label="$t('app.campaign.results.headerButtons.exportPDF')" :disabled="isPdfExportDisabled" :loading="pdfIsLoading" :dropdown="pdfDropdown" v-tooltip="pdfExportTooltip" @click="exportPdf">
          <template #prefix>
            <ExportPdfButtonIcon style="width: 16px"></ExportPdfButtonIcon>
          </template>
        </DropdownButton>
        <Button data-step="13" :disabled="!state.selectedParticipants.length" class="icon icon-left outline-white light-text" :label="$t('app.campaign.results.headerButtons.detailedResults')" @click="openDetailedResults" v-tooltip="detailedResultsTooltip">
          <template #prefix>
            <DetailedResultButtonIcon style="width: 16px"></DetailedResultButtonIcon>
          </template>
        </Button>

        <Button class="icon icon-left outline-white light-text put-to-end" :label="$t('app.campaign.results.headerButtons.filter')" @click="emits('openResultFilters')" :badge="filterCount">
          <template #prefix>
            <FilterButtonIcon style="width: 16px;"></FilterButtonIcon>
          </template>
        </Button>
        <VField class="light" :placeholder="$t('app.campaign.single.search')" name="search" icon="bi-search" v-model="state.search.value" @input="(event) => (state.search.value = event.target.value)" />
      </div>
      <div class="results-table">
        <Table
          v-if="!ponderationIsVisible"
          :data="filteredParticipants"
          :fields="tableFields"
          :search="state.search"
          :actions="false"
          :bulk="{ actions: [] }"
          :removeBulkActions="true"
          :rowClick="false"
          :rowClickToSelect="true"
          :itemsPerPage="100"
          @selectedRow="updateSelectedRows"
        >
          <template #[`results.compatibility`]="{ data }">
            <div class="container center-field">
              <ProgressCircle :isNone="data?.results?.compatibility === undefined" data-step="12" :size="36" :stroke="5" :gapAngle="90" :percentage="(data?.results?.compatibility as number) * 100" />
            </div>
          </template>
          <template #[`candidate.last_name`]="{ data }">
            <div class="container center-field">
              <span v-safe-html="`${data?.candidate?.first_name || data?.pending_first_name || ''} ${data?.candidate?.last_name || data?.pending_last_name || ''}`" />
            </div>
          </template>
          <template #[`results.completed_at`]="{ data }">
            <div class="container center-field">
              <span v-text="data.results?.completed_at ? getDate(data.results.completed_at, 'LL') : $t('app.campaign.results.noDate')" />
            </div>
          </template>
          <template #empty>
            <Empty
              :message="$t('app.campaign.finishedTable.noResults')"
              :action="{
                label: $t('app.campaign.finishedTable.seeActivableTalents'),
                click: () => {
                  appStore.changeTab('Tracking');
                },
              }"
              :dark="isDark"
              icon="loop"
            />
          </template>
        </Table>
      </div>
      <ActionModal :modal="state.actionModal.modal" :data="state.participants" :isOpen="state.actionModal.show" @close="state.actionModal.show = false" @confirm="state.actionModal.show = false" />
      <HelpModal :isOpen="state.showHelpModal" @close="state.showHelpModal = !state.showHelpModal" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
  .results-table {
    width: 100%;
    height: 100%;
    margin-top: 16px;
  }

  .center-field {
    justify-content: center !important;
  }

  .button-header {
    display: flex;
    flex-direction: row;
    gap: 8px;

    .btn {
      height: 46px;
    }

    .put-to-end {
      margin-left: auto;
    }
  }
</style>

<script setup lang="ts">
import { storeToRefs } from "pinia";
import { reactive, computed, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";
import { useDark } from "@vueuse/core";

import { getDate, dateIsBefore } from "@/composables/utils";

import { useAppStore } from "@/stores/app.js";

import Empty from "@/components/table/Empty.vue";
import Table from "@/components/global/Table.vue";
import ActionModal from "@/components/modals/app/actionModal/ActionModal.vue";
import HelpModal from "@/components/modals/app/helpModal/HelpModal.vue";
import DropdownButton from "@/components/global/DropdownButton.vue";
import { DropdownItem } from "@/components/global/DropdownButton.vue";

import { Filters } from "../results/ResultsFilters.vue";

import ExportPdfButtonIcon from "@/assets/images/icon/campaign/results/export_pdf.svg?component";
import ExportCsvButtonIcon from "@/assets/images/icon/campaign/results/export_csv.svg?component";
import DetailedResultButtonIcon from "@/assets/images/icon/campaign/results/detailed_results.svg?component";
import FilterButtonIcon from '@/assets/images/icon/campaign/filter.svg?component';

import { useParticipations } from "@/queries/participant.query";
import { useCampaign } from "@/queries/campaigns.query";
import { Participant } from "@/types/participant";

const isDark = useDark();
const route = useRoute();
const appStore = useAppStore();
const { t } = useI18n();
const { isCampaignV1, isCampaignV4OrMore } = useSkillsStore();

const { ponderationIsVisible } = storeToRefs(appStore);

const { data: participations, isPending } = useParticipations(appStore.onboardingIsLive);
const { data: campaign, isPending: isCampaignPending } = useCampaign(appStore.onboardingIsLive);

const pdfDropdown = computed<DropdownItem[]>(() => {
  if (isCampaignV4OrMore(campaign.value)) {
    return [
      {
        id: 'organisation-report-v4',
        label: t('app.campaign.newPage.slidingPanel.options.report.v4Report')
      }
    ];
  } else {
    return [
      {
        id: "organisation-simplified-report",
        label: t("app.campaign.newPage.slidingPanel.options.report.simplified"),
        disabled: isCampaignV1(campaign.value),
      },
      {
        id: "organisation-full-report",
        label: t("app.campaign.newPage.slidingPanel.options.report.complete"),
        disabled: false,
      },
    ];
  }
});

const props = defineProps<{
  search?: Object,
  filters?: Filters,
}>();

const emits = defineEmits(["openSelectedResults", "openResultFilters"]);

const state = reactive({
  actionModal: {
    show: false,
    modal: "",
  },
  previewIsOpen: false,
  search: {
    value: "",
    searchable: [
      {
        combined: true,
        keys: ["pending_first_name", "pending_last_name"],
      },
      "pending_email",
      {
        combined: true,
        keys: ["candidate.first_name", "candidate.last_name"],
      },
      "candidate.email",
    ],
  },
  selectedParticipants: [],
  openedTalentResults: [],
  showHelpModal: false,
  participants: [],
});

const tableFields = computed(() => {
  return [
    {
      id: "results.compatibility",
      label: t("app.campaign.results.table.compatibility"),
      defaultSortDir: "DESC",
      center: true,
      sort(a, b, dir) {
        if (dir === "ASC") return a - b;
        return b - a;
      },
    },
    {
      id: "candidate.last_name",
      fallback: "pending_last_name",
      label: t("app.campaign.results.table.talent"),
      center: true,
      sort(a, b, dir) {
        if (dir === "ASC") return a.localeCompare(b);
        return b.localeCompare(a);
      },
    },
    {
      id: "results.completed_at",
      label: t("app.campaign.results.table.evaluationDate"),
      center: true,
      sort(a, b, dir) {
        if (dir === "ASC") {
          return dateIsBefore(b, a) ? 1 : -1;
        } else {
          return dateIsBefore(a, b) ? 1 : -1;
        }
      },
    },
  ];
});

function getParticipantStyles(participant: Participant) {
  if (!participant?.results) return [];
  const styleEntries = Object.entries(participant?.results.styles);

  return styleEntries.reduce((acc, entry) => {
    const styleName = entry[0];
    acc.push({
      skill: styleName,
      style: entry[1] as string,
    });
    return acc;
  }, []);
}

const isPdfExportDisabled = computed(() => {
  return (!state.selectedParticipants.length ||
    state.selectedParticipants.length > 1
  );
});

const pdfExportTooltip = computed(() => {
  if (!state.selectedParticipants.length) {
    return t('app.campaign.results.headerButtons.selectOneTooltip');
  }
  if (state.selectedParticipants.length > 1) {
    return t('app.campaign.results.headerButtons.selectOnlyOneTooltip');
  }
  return undefined;
})

const detailedResultsTooltip = computed(() => {
  if (!state.selectedParticipants.length) {
    return t('app.campaign.results.headerButtons.selectOneOrMoreTooltip');
  }
  return undefined;
});

const filteredParticipants = computed(() => {
  if (isPending.value) return [];
  return participations.value.filter((participant) => {
    if (!participant.results) return false;
    if (!props.filters) return true;

    // Filter by date
    const creationDate = new Date((participant.creation_time as string).replace(/Z$/, ""));
    const resultsCreationDate = new Date((participant.results.completed_at as string).replace(/Z$/, ""));
    if (props?.filters?.invitationDateFrom && creationDate.getTime() < props?.filters?.invitationDateFrom.getTime()) return false;
    if (props?.filters?.invitationDateTo && creationDate.getTime() > props?.filters?.invitationDateTo.getTime()) return false;

    if (props?.filters?.evaluationEndDateFrom && resultsCreationDate.getTime() < props?.filters?.evaluationEndDateFrom.getTime()) return false;
    if (props?.filters?.evaluationEndDateTo &&  resultsCreationDate.getTime() > props?.filters?.evaluationEndDateTo.getTime()) return false;

    // Filter by compatibility
    if ((participant?.results?.compatibility * 100) < props.filters.compatibilityScore[0] || (participant?.results?.compatibility * 100) > props.filters.compatibilityScore[1]) return false;
    
    // Filter by score
    const participantScoreEntries = Object.entries(participant?.results.scores).filter((v) => {
      if (v[1] <= -1) return false;
      if (v[1] === null) return false;
      return true;
    });
    if (props.filters.skills) {
      for (const scoreEntry of participantScoreEntries) {
        const scoreId = scoreEntry[0];
        const scoreValue = scoreEntry[1];
        const scoreMinMax = props.filters.skills[scoreId];
        if (!!scoreMinMax && ((scoreValue * 100) < scoreMinMax[0] || (scoreValue * 100) > scoreMinMax[1])) return false;
      }
    }

    // Filter by styles
    if (props.filters.selectedStyles) {
      const styles = getParticipantStyles(participant);
      for (const style of styles) {
        if (props.filters.selectedStyles[style.skill].length) {
          const participantHasFilteredStyle = props.filters.selectedStyles[style.skill].some((wording) => wording === style.style);
          if (!participantHasFilteredStyle) return false;
        }
      }
    }

    // Filter by badges
    if (props.filters.selectedBadges) {
      const participantBadges = Object.entries(participant?.results.badges).filter((entry) => {
        if (entry[1] === false) return false;
        return true;
      }).reduce((acc, entry) => {
        acc.push(entry[0]);
        return acc;
      }, []);
      const selectedBadgeEntries = Object.entries(props.filters.selectedBadges).filter((entry) => entry[1]);

      if (selectedBadgeEntries.length) {
        const some = selectedBadgeEntries.some((selectedBadgeEntry) => {
          const participantHasThisBadge = participantBadges.some((badge) => {
            if (selectedBadgeEntry[0] === badge) return true;
            return false;
          });
          return participantHasThisBadge;
        });
        if (!some) return false;
      }
    }
    return true;
  }).sort((pA, pB) => pB.results.compatibility - pA.results.compatibility);
});

const filterCount = computed(() => {
  let count = 0;
  if (props?.filters?.compatibilityScore) {
    if (props?.filters?.compatibilityScore[0] !== 0 || props?.filters?.compatibilityScore[1] !== 99) {
      count += 1;
    }
  }

  if (!!props?.filters?.evaluationEndDateFrom || !!props?.filters?.evaluationEndDateTo) count += 1;
  if (!!props?.filters?.invitationDateFrom || !!props?.filters?.invitationDateTo) count += 1;

  if (props?.filters?.skills) {
    const haveSomeSkillFilter = Object.values(props?.filters?.skills).some((skill) => {
      return skill[0] !== 0 || skill[1] !== 100;
    });
    if (haveSomeSkillFilter) count += 1;
  }

  if (props?.filters?.selectedStyles) {
    const haveSomeStyleFilter = Object.values(props?.filters?.selectedStyles).some((style) => {
      return !!style.length;
    });
    if (haveSomeStyleFilter) count += 1;
  }

  if (props?.filters?.selectedBadges) {
    const haveSomeBadgeFilter = Object.values(props?.filters?.selectedBadges).some((style) => {
      return style;
    });
    if (haveSomeBadgeFilter) count += 1;
  }
  return count;
});

function openDetailedResults() {
  appStore.openedCandidateResults = state.selectedParticipants;
  appStore.talentResultPanelIsOpen = true;
}

function updateSelectedRows(rows) {
  state.selectedParticipants = rows;
}

const pdfIsLoading = ref(false);

async function exportPdf(dropdownItem: DropdownItem) {
  pdfIsLoading.value = true;
  try {
    const pdf = await appStore.exportPdf(dropdownItem.id, state.selectedParticipants[0].participant_id);
    const fileURL = URL.createObjectURL(new Blob([pdf], { type: 'application/pdf' }));
    window.open(fileURL, '_blank');
  } finally {
    pdfIsLoading.value = false;
  }
}

const csvIsLoading = ref(false);

async function exportCSV() {
  csvIsLoading.value = true;
  try {
    const csvContent = await appStore.exportCsv(campaign.value.id);
    const blob = new Blob([csvContent], {
      type: 'text/csv;charset=utf-8;'
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.setAttribute('download', `${campaign.value.campaign.internal_name}_${new Date().toISOString()}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } finally {
    csvIsLoading.value = false;
  }
}

onMounted(() => {
  if (route.name === "campaign") {
  }
});

</script>
